import { useState, useCallback, useMemo } from 'react';
import authService, { ActivatedPack, ActivatedPacksResponse, Store as StoreType } from '../services/authService';
import { buildProcessedTicketDataList } from '../utils/ticketFactory';

interface UseStoreActivatedPacksProps {
  storeId: string;
  selectedLocationId?: string;
}

export const useStoreActivatedPacks = ({ storeId, selectedLocationId }: UseStoreActivatedPacksProps) => {
  const [packsData, setPacksData] = useState<ActivatedPacksResponse | null>(null);
  const [storeData, setStoreData] = useState<StoreType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const processedPacks = useMemo(() => {
    if (!packsData?.items) return [];
    return buildProcessedTicketDataList(packsData.items, {
      format: packsData.barcode_format,
      barcodeLength: packsData.barcode_length,
      scannerFormats: packsData.barcode_scanner_formats,
      direction: packsData.direction,
      regionStartingTicketNumber: packsData.region_starting_ticket_number,
    });
  }, [packsData]);

  const loadStoreAndPacks = useCallback(async () => {
    if (!storeId) return;
    try {
      setLoading(true);
      setError(null);

      const [storeResp, packsResp] = await Promise.all([
        authService.getStoreDetails(storeId),
        authService.getActivatedPacks(storeId, selectedLocationId || undefined),
      ]);

      setStoreData(storeResp || null);
      setPacksData(packsResp || null);

      // Save to localStorage for compatibility
      if (storeResp) {
        localStorage.setItem('storeData', JSON.stringify(storeResp));
      }
    } catch (err) {
      console.error('useStoreActivatedPacks error:', err);
      setError('Failed to load store or activated packs');
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, storeId]);

  const addNewPack = useCallback((newPack: ActivatedPack) => {
    setPacksData(prev => ({
      ...(prev || { items: [], total: 0 }),
      barcode_format: prev?.barcode_format,
      barcode_length: prev?.barcode_length,
      barcode_scanner_formats: prev?.barcode_scanner_formats,
      direction: prev?.direction,
      items: [newPack, ...(prev?.items || [])],
      total: (prev?.total || 0) + 1,
    }));
  }, []);

  const removePack = useCallback((packId: string) => {
    setPacksData(prev => {
      if (!prev) return prev;
      const nextItems = prev.items.filter(p => p.pack_id !== packId);
      return {
      ...prev!,
        items: nextItems,
        total: Math.max(0, prev.total - (prev.items.length - nextItems.length)),
      };
    });
  }, []);

  return {
    packsData,
    storeData,
    processedPacks,
    loading,
    error,
    setError,
    loadStoreAndPacks,
    addNewPack,
    removePack,
  };
};

export default useStoreActivatedPacks;
